import React, { useContext, Fragment, useState, useRef, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import ButtonBase from '@material-ui/core/ButtonBase';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import UpdateIcon from '@material-ui/icons/Update';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { AppContext, LayoutContext } from '../../common/contexts';
import {
    // INTERVIEW_STATUS_PENDING,
    INTERVIEW_STATUS_RESCHEDULE_JS,
    INTERVIEW_STATUS_RESCHEDULE_EMP,
    INTERVIEW_STATUS_DECLINED,
    INTERVIEW_STATUS_CONFIRMED,
    INTERVIEW_STATUS_CANCELLED,
} from '../../common/constants';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getIsShowCalendarTileBatch } from '../../common/calendarHelper';

import { loadInterviewCard, loadInterviewNote } from '../../common/services/empMyCalendarServices';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import InterviewCard from './InterviewCard';

import cx from 'clsx';
import { format } from 'date-fns';
import _get from 'lodash/get';

const styles = theme => ({
    root: {
        minHeight: '52px',
        margin: '0px 2px 4px 2px',
        padding: '9px 8px 9px 8px',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    },
    interviewContainer: { width: '100%', display: 'flex', [theme.breakpoints.down('sm')]: { justifyContent: 'center', alignItems: 'center' } },
    timeContainer: { flexGrow: 1, textAlign: 'left' },
    nameContainer: { textAlign: 'left' },
    declinedText: { textDecoration: 'line-through' },
    statusIconContainer: { flexGrow: 1, textAlign: 'right' },
    pendingIconContainer: { color: 'rgba(17, 30, 20, 0.4)' },
    rescheduleIconContainer: { color: '#FC821F' },
    confirmedIconContainer: { color: '#FC821F' },
    declinedIconContainer: { color: '#F0F2F6' },
    pending: {
        background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03) 5px, #FFF 5px, #FFF 10px)',
        '&:hover': {
            background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, #FFF 5px, #FFF 10px)',
        },
    },
    pendingSelected: {
        background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, #FFF 5px, #FFF 10px)',
    },
    reschedule: {
        background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.06), rgba(125, 46, 2, 0.06) 5px, #FFF 5px, #FFF 10px)',
        '&:hover': {
            background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.1), rgba(125, 46, 2, 0.1) 5px, #FFF 5px, #FFF 10px)',
        },
    },
    rescheduleSelected: {
        background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.1), rgba(125, 46, 2, 0.1) 5px, #FFF 5px, #FFF 10px)',
    },
    declined: {
        border: '1px solid #F0F2F6',
        backgroundColor: '#FFF',
        '&:hover': {
            border: '1px solid #D8DBE1',
            backgroundColor: '#FFF',
        },
    },
    declinedSelected: {
        border: '1px solid #D8DBE1',
        backgroundColor: '#FFF',
    },
    confirmed: {
        backgroundColor: '#FFF8E5',
        '&:hover': {
            backgroundColor: '#FFF1CB',
        },
    },
    confirmedSelected: {
        backgroundColor: '#FFF1CB',
    },
});

const ICON_STYLES = { width: '16px', height: '16px', marginTop: '2px' };

const getDefns = params => {
    const { props, employerId, interviewCardAnchorEl, interviewCardData, interviewNote, interviewCardOpen, isShowBatch } = params;
    const {
        setInterviewCardData,
        setInterviewNote,
        setSelectedDate,
        setInterviewCardOpen,
        setInterviewCardAnchorEl,
        setLoadInterviewCardUnsubscribeRef,
        setLoadInterviewNoteUnsubscribeRef,
        detachInterviewCardListener,
    } = params;
    const {
        classes,
        classes: {
            root: rootCls,
            statusIconContainer: statusIconContainerCls,
            pendingIconContainer: pendingIconContainerCls,
            rescheduleIconContainer: rescheduleIconContainerCls,
            confirmedIconContainer: confirmedIconContainerCls,
            declinedIconContainer: declinedIconContainerCls,
            pending: pendingCls,
            pendingSelected: pendingSelectedCls,
            reschedule: rescheduleCls,
            rescheduleSelected: rescheduleSelectedCls,
            declined: declinedCls,
            declinedSelected: declinedSelectedCls,
            confirmed: confirmedCls,
            confirmedSelected: confirmedSelectedCls,
            timeContainer: timeContainerCls,
            nameContainer: nameContainerCls,
            declinedText: declinedTextCls,
        },
        interviewData,
        dt,
    } = props;

    const interviewId = _get(interviewData, 'interviewId');
    const isSelected = interviewCardOpen; //interviewId === clickedInterviewId;
    const status = _get(interviewData, 'status');
    const startDt = _get(interviewData, 'startDt');
    const startTime = startDt ? format(startDt.toDate(), 'h:mm') : '??:??';
    const startAmPm = startDt ? format(startDt.toDate(), 'a') : '??:??';
    const endDt = _get(interviewData, 'endDt');
    const endTime = endDt ? format(endDt.toDate(), 'h:mm') : '??:??';
    const amPm = endDt ? format(endDt.toDate(), 'a') : '??:??';
    const jobTitle = _get(interviewData, 'jobTitle');
    const companyName = _get(interviewData, 'companyName');

    const isEmployer = !!employerId;

    let rootClasses = [rootCls];
    let statusIconContainerClasses = [statusIconContainerCls];
    let timeContainerClasses = [timeContainerCls];
    let nameContainerClasses = [nameContainerCls];
    let interviewBlockTooltipTitle = 'Interview Request';
    switch (status) {
        case INTERVIEW_STATUS_RESCHEDULE_JS:
        case INTERVIEW_STATUS_RESCHEDULE_EMP: {
            if (isSelected) {
                rootClasses.push(rescheduleSelectedCls);
            } else {
                rootClasses.push(rescheduleCls);
            }
            statusIconContainerClasses.push(rescheduleIconContainerCls);
            interviewBlockTooltipTitle = 'Reschedule Pending';
            break;
        }
        case INTERVIEW_STATUS_DECLINED:
        case INTERVIEW_STATUS_CANCELLED: {
            if (isSelected) {
                rootClasses.push(declinedSelectedCls);
            } else {
                rootClasses.push(declinedCls);
            }
            statusIconContainerClasses.push(declinedIconContainerCls);
            timeContainerClasses.push(declinedTextCls);
            nameContainerClasses.push(declinedTextCls);
            interviewBlockTooltipTitle = status === INTERVIEW_STATUS_DECLINED ? 'Interview Declined' : 'Interview Cancelled';
            break;
        }
        case INTERVIEW_STATUS_CONFIRMED: {
            if (isSelected) {
                rootClasses.push(confirmedSelectedCls);
            } else {
                rootClasses.push(confirmedCls);
            }
            statusIconContainerClasses.push(confirmedIconContainerCls);
            interviewBlockTooltipTitle = 'Interview Confirmed';
            break;
        }
        default: {
            // INTERVIEW_STATUS_PENDING
            if (isSelected) {
                rootClasses.push(pendingSelectedCls);
            } else {
                rootClasses.push(pendingCls);
            }
            statusIconContainerClasses.push(pendingIconContainerCls);
        }
    }

    let defns = {
        root: {
            className: cx(rootClasses), // condition inside classnames didn't work for some reasons... so did it outside
            onClick: handleClickInterview.bind(
                null,
                isEmployer,
                interviewId,
                setInterviewCardAnchorEl,
                setInterviewCardData,
                setInterviewCardOpen,
                setInterviewNote,
                setSelectedDate.bind(null, dt),
                setLoadInterviewCardUnsubscribeRef,
                setLoadInterviewNoteUnsubscribeRef,
            ),
        },
        interviewBlockTooltip: {
            title: interviewBlockTooltipTitle,
            placement: 'top-end',
            arrow: true,
        },
        timeText: {
            variant: 'caption',
            value: `${startTime}${startAmPm.toLowerCase()}-${endTime}${amPm.toLowerCase()}`,
        },
        nameText: {
            variant: 'subtitle2',
            value: employerId ? jobTitle : companyName,
        },
        statusIconContainer: {
            className: cx(statusIconContainerClasses),
        },
        timeContainer: {
            className: cx(timeContainerClasses),
        },
        nameContainer: {
            className: cx(nameContainerClasses),
        },
        interviewCard: {
            anchorEl: interviewCardAnchorEl,
            open: interviewCardOpen,
            data: interviewCardData,
            note: interviewNote,
            setInterviewCardOpen,
            setInterviewCardData,
            setInterviewNote,
            detachInterviewCardListener,
            onClose: handleCloseInterviewCard.bind(null, setInterviewCardOpen),
        },
        badge: {
            color: 'primary',
            badgeContent: ' ',
            variant: 'dot',
            invisible: !isShowBatch,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

const getStatusIcon = props => {
    const status = _get(props, 'interviewData.status');
    switch (status) {
        case INTERVIEW_STATUS_RESCHEDULE_JS:
        case INTERVIEW_STATUS_RESCHEDULE_EMP: {
            return <UpdateIcon style={ICON_STYLES} />;
        }
        case INTERVIEW_STATUS_CONFIRMED: {
            return <CheckCircleIcon style={ICON_STYLES} />;
        }
        case INTERVIEW_STATUS_DECLINED:
        case INTERVIEW_STATUS_CANCELLED: {
            return <HighlightOffIcon style={ICON_STYLES} />;
        }
        default: {
            // INTERVIEW_STATUS_PENDING
            return <HelpOutlineIcon style={ICON_STYLES} />;
        }
    }
};

const handleClickInterview = (
    isEmployer,
    interviewId,
    setInterviewCardAnchorEl,
    setInterviewCardData,
    setInterviewCardOpen,
    setInterviewNote,
    setSelectedDate,
    setLoadInterviewCardUnsubscribeRef,
    setLoadInterviewNoteUnsubscribeRef,
    evt,
) => {
    evt.preventDefault();
    setInterviewCardAnchorEl(evt.target);
    // setClickedInterviewId(interviewId); //('dWIZrhfHLzF2xkodwVy1');
    const unsubscribe = loadInterviewCard(interviewId, setInterviewCardData, setInterviewCardOpen);
    const unsubscribeLoadInterviewNote = loadInterviewNote({ isEmployer, interviewId, setInterviewNote });

    setLoadInterviewCardUnsubscribeRef(unsubscribe);
    setLoadInterviewNoteUnsubscribeRef(unsubscribeLoadInterviewNote);
    setSelectedDate();
};

const handleCloseInterviewCard = setInterviewCardOpen => {
    setInterviewCardOpen(false);
};

const InterviewBlock = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const employerId = _get(appContext, 'employerId');
    const context = useContext(LayoutContext);
    const setSelectedDate = _get(context, 'setSelectedDate');
    const dashboardData = _get(context, 'dashboardData');
    const loadInterviewCardUnsubscribeRef = useRef();
    const loadInterviewNoteUnsubscribeRef = useRef();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const { isPopover } = props;
    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [interviewCardAnchorEl, setInterviewCardAnchorEl] = useState(null);
    const [interviewCardData, setInterviewCardData] = useState();
    const [interviewNote, setInterviewNote] = useState();
    const [interviewCardOpen, setInterviewCardOpen] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const isHidden = _get(props, 'isHidden');
    const interviewId = _get(props, 'interviewData.interviewId');

    //---------------------------------------------------------------------
    // useCallback hook
    //---------------------------------------------------------------------
    const setLoadInterviewCardUnsubscribeRef = useCallback(unsubscribe => {
        loadInterviewCardUnsubscribeRef.current = unsubscribe;
    }, []);
    const setLoadInterviewNoteUnsubscribeRef = useCallback(unsubscribe => {
        loadInterviewNoteUnsubscribeRef.current = unsubscribe;
    }, []);
    const detachInterviewCardListener = useCallback(() => {
        if (loadInterviewCardUnsubscribeRef.current) {
            // call unsubscribe
            loadInterviewCardUnsubscribeRef.current();
        }
        if (loadInterviewNoteUnsubscribeRef.current) {
            // call unsubscribe
            loadInterviewNoteUnsubscribeRef.current();
        }
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isShowBatch = getIsShowCalendarTileBatch({ interviewId, dashboardData });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        employerId,
        interviewCardAnchorEl,
        setInterviewCardAnchorEl,
        interviewCardData,
        setInterviewCardData,
        interviewNote,
        setInterviewNote,
        setSelectedDate,
        interviewCardOpen,
        setInterviewCardOpen,
        setLoadInterviewCardUnsubscribeRef,
        setLoadInterviewNoteUnsubscribeRef,
        detachInterviewCardListener,
        isShowBatch,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return isHidden ? null : (
        <Fragment>
            <Tooltip {...defns.interviewBlockTooltip}>
                <ButtonBase {...defns.root}>
                    <div {...defns.interviewContainer}>
                        {smMatches ? (
                            isPopover && (
                                <div {...defns.timeContainer}>
                                    <AfitdTypography {...defns.timeText} />
                                </div>
                            )
                        ) : (
                            <div {...defns.timeContainer}>
                                <AfitdTypography {...defns.timeText} />
                            </div>
                        )}

                        <Badge {...defns.badge}>
                            <div {...defns.statusIconContainer}>{getStatusIcon(props)}</div>
                        </Badge>
                    </div>
                    {smMatches ? (
                        isPopover && (
                            <div {...defns.nameContainer}>
                                <AfitdTypography {...defns.nameText} />
                            </div>
                        )
                    ) : (
                        <div {...defns.nameContainer}>
                            <AfitdTypography {...defns.nameText} />
                        </div>
                    )}
                </ButtonBase>
            </Tooltip>
            {interviewCardOpen && <InterviewCard {...defns.interviewCard} />}
        </Fragment>
    );
};

export default withStyles(styles)(InterviewBlock);
